import type { SymbolAsset } from '../symbols/model';
import { isAssetGraphic, isImageGraphic, symbolLibraryIdentity } from '../symbols/model';
import {
  sceneSymbolRequests,
  effectivePointSymbols,
  renderableAnnotationLayers,
  pointPresentation,
} from '../symbols/scene';
import { graphicKey, graphicTint } from '../symbols/tint';
import {
  EMPTY_SYMBOL_CATALOGUE,
  SymbolImageCache,
  type PreparedSymbolCatalogue,
} from '../symbols/images';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';

import type { SourceDescriptor, SupportedGeometry } from '../data/model';
import type { LayerDefinition } from '../project/model';
import type {
  AnnotationCoordinate,
  AnnotationCreationTool,
  AnnotationLayer,
  AnnotationMovePart,
} from '../annotations/model';
import { MapLibreRenderingAdapter } from './MapLibreRenderingAdapter';
import type { MapView } from './model';
import type { ImportRectangleState } from './importRectangle';
import type { GroundFootprint } from '../composition/inset';
import type {
  MapRenderingAdapter,
  MapRenderingAdapterFactory,
  MapViewChangeMetadata,
} from './renderingAdapter';
import type { RenderedStyleInspection } from './renderingAdapter';
import type { InspectedFeature } from '../popups/inspection';
import type { MapSelectionMutation, SelectedFeatureRef, SelectionMethod } from '../selection/model';
import {
  DEFAULT_BASEMAP_CONFIGURATION,
  interactiveBasemapAttribution,
  type BasemapConfiguration,
} from '../basemaps/catalog';

const createDefaultAdapter: MapRenderingAdapterFactory = () => new MapLibreRenderingAdapter();
const EMPTY_ITEMS: readonly never[] = [];

interface MapSurfaceProps {
  readonly importRectangle?: ImportRectangleState | null;
  readonly interactive?: boolean;
  readonly renderWorldCopies?: boolean;
  readonly onGroundFootprint?: (footprint: GroundFootprint | null, zoom: number) => void;
  readonly basemap?: BasemapConfiguration;
  readonly backgroundColour: string;
  readonly view: MapView;
  readonly searchGeometry?: SupportedGeometry | null;
  readonly sources: readonly SourceDescriptor[];
  readonly symbolAssets?: readonly SymbolAsset[];
  readonly layers: readonly LayerDefinition[];
  readonly annotationLayers?: readonly AnnotationLayer[];
  readonly selectedAnnotationId?: string | null;
  readonly activeFocusAreaId?: string | null;
  readonly annotationRenderScale?: number;
  readonly presentationScale?: number;
  readonly onViewChange: (view: MapView, metadata: MapViewChangeMetadata) => void;
  readonly minimumZoom?: number;
  readonly maximumZoom?: number;
  readonly pitchEnabled?: boolean;
  readonly showAttribution?: boolean;
  readonly inspectedFeature?: InspectedFeature | null;
  readonly featureInspectionEnabled?: boolean;
  readonly onFeatureInspect?: (feature: InspectedFeature | null) => void;
  readonly featureSelectionEnabled?: boolean;
  readonly featureSelectionMethod?: SelectionMethod | null;
  readonly selectedFeatures?: readonly SelectedFeatureRef[];
  readonly onFeatureSelect?: (
    feature: SelectedFeatureRef | null,
    mutation: MapSelectionMutation,
  ) => void;
  readonly onFeaturesSelect?: (
    features: readonly SelectedFeatureRef[],
    mutation: MapSelectionMutation,
  ) => void;
  readonly createAdapter?: MapRenderingAdapterFactory;
  readonly annotationAuthoringEnabled?: boolean;
  readonly annotationCreationTool?: AnnotationCreationTool | null;
  readonly activeAnnotationLayerId?: string | null;
  readonly annotationDraftCoordinates?: readonly AnnotationCoordinate[];
  readonly onAnnotationCoordinate?: (coordinate: AnnotationCoordinate, finish: boolean) => void;
  readonly onAnnotationDeselect?: () => void;
  readonly onAnnotationSelect?: (layerId: string, annotationId: string) => void;
  readonly onAnnotationMove?: (
    layerId: string,
    annotationId: string,
    longitudeDelta: number,
    latitudeDelta: number,
    part?: AnnotationMovePart,
    widthDelta?: number,
    heightDelta?: number,
  ) => void;
}

export function MapSurface({
  importRectangle = null,
  interactive = true,
  renderWorldCopies = true,
  onGroundFootprint,
  basemap = DEFAULT_BASEMAP_CONFIGURATION,
  backgroundColour,
  view,
  searchGeometry = null,
  sources,
  symbolAssets = EMPTY_ITEMS,
  layers,
  annotationLayers = EMPTY_ITEMS,
  selectedAnnotationId = null,
  activeFocusAreaId = null,
  annotationRenderScale = 1,
  presentationScale = 1,
  onViewChange,
  minimumZoom,
  maximumZoom,
  pitchEnabled = true,
  showAttribution = true,
  inspectedFeature = null,
  featureInspectionEnabled = false,
  onFeatureInspect,
  featureSelectionEnabled = false,
  featureSelectionMethod,
  selectedFeatures = EMPTY_ITEMS,
  onFeatureSelect,
  onFeaturesSelect,
  createAdapter = createDefaultAdapter,
  annotationAuthoringEnabled = false,
  annotationCreationTool = null,
  activeAnnotationLayerId = null,
  annotationDraftCoordinates = EMPTY_ITEMS,
  onAnnotationCoordinate,
  onAnnotationSelect,
  onAnnotationDeselect,
  onAnnotationMove,
}: MapSurfaceProps) {
  const footprintCallbackRef = useRef(onGroundFootprint);
  const observesGround = onGroundFootprint !== undefined;
  useEffect(() => {
    footprintCallbackRef.current = onGroundFootprint;
  }, [onGroundFootprint]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef<MapRenderingAdapter | null>(null);
  const backgroundColourRef = useRef(backgroundColour);
  const basemapRef = useRef(basemap);
  const viewRef = useRef(view);
  const [status, setStatus] = useState<'initialising' | 'ready' | 'error'>('initialising');
  const [basemapStatus, setBasemapStatus] = useState<'loading' | 'ready' | 'error'>('ready');
  const [renderedFeatureCount, setRenderedFeatureCount] = useState(0);
  const [renderedLabelCount, setRenderedLabelCount] = useState(0);
  const [rendered3dBuildingCount, setRendered3dBuildingCount] = useState(0);
  const [renderedTopLayerId, setRenderedTopLayerId] = useState('');
  const [inspectionHighlight, setInspectionHighlight] = useState('');
  const [selectionHighlightCount, setSelectionHighlightCount] = useState(0);
  const attribution = interactiveBasemapAttribution(basemap);
  const [renderedStyle, setRenderedStyle] = useState<RenderedStyleInspection>({
    backgroundColour,
    colours: [],
    polygonLabelAnchorCount: 0,
    layerIds: [],
    signature: '',
  });
  const [preparedSymbols, setPreparedSymbols] = useState<{
    assetsKey: string;
    signature: string;
    images: PreparedSymbolCatalogue;
  }>({ assetsKey: '', signature: '', images: EMPTY_SYMBOL_CATALOGUE });
  const requests = useMemo(
    () => sceneSymbolRequests(layers, annotationLayers),
    [layers, annotationLayers],
  );
  const symbolSignature = JSON.stringify(
    [...new Set(requests.map((r) => graphicKey(r.graphic, r.tint, r)))].sort(),
  );
  const requestsRef = useRef(requests);
  const assetsRef = useRef(symbolAssets);
  const usedAssetIds = new Set(
    requests.flatMap((r) => (isAssetGraphic(r.graphic) ? [r.graphic.assetId] : [])),
  );
  const assetsKey = symbolLibraryIdentity(symbolAssets.filter((a) => usedAssetIds.has(a.id)));
  useEffect(() => {
    requestsRef.current = requests;
    assetsRef.current = symbolAssets;
  }, [requests, symbolAssets]);
  const [symbolError, setSymbolError] = useState('');
  const symbolCache = useMemo(() => new SymbolImageCache(), []);
  useEffect(() => () => symbolCache.clear(), [symbolCache, sources]);
  useEffect(() => {
    const abort = new AbortController();
    const cache = symbolCache;
    void cache
      .graphics(
        requestsRef.current,
        assetsRef.current,
        Math.min(4, Math.max(2, window.devicePixelRatio || 1)),
        abort.signal,
      )
      .then((images) => {
        if (!abort.signal.aborted) {
          setPreparedSymbols({ assetsKey, signature: symbolSignature, images });
          setSymbolError('');
        }
      })
      .catch(() => {
        if (!abort.signal.aborted) setSymbolError('A project symbol could not be prepared.');
      });
    return () => {
      abort.abort();
    };
  }, [assetsKey, symbolCache, sources, symbolSignature]);
  const symbolImages =
    preparedSymbols.assetsKey === assetsKey && preparedSymbols.signature === symbolSignature
      ? preparedSymbols.images
      : EMPTY_SYMBOL_CATALOGUE;
  const renderedLayers = useMemo(
    () =>
      layers.filter((layer) =>
        effectivePointSymbols(layer.style).every(
          (point) =>
            !isImageGraphic(point.shape) ||
            symbolImages.has(
              graphicKey(
                point.shape,
                graphicTint(point.shape, point.fillColor, point.tint),
                pointPresentation(point),
              ),
            ),
        ),
      ),
    [layers, symbolImages],
  );
  const renderedAnnotations = useMemo(
    () => renderableAnnotationLayers(annotationLayers, symbolImages),
    [annotationLayers, symbolImages],
  );
  const scene = useMemo(
    () => ({
      sources,
      layers: renderedLayers,
      symbolImages,
      annotationLayers: renderedAnnotations,
      selectedAnnotationId,
      activeFocusAreaId,
      annotationRenderScale,
      presentationScale,
      searchGeometry,
    }),
    [
      sources,
      renderedLayers,
      symbolImages,
      renderedAnnotations,
      selectedAnnotationId,
      activeFocusAreaId,
      annotationRenderScale,
      presentationScale,
      searchGeometry,
    ],
  );
  const sceneRef = useRef(scene);
  const onFeatureInspectRef = useRef(onFeatureInspect);
  const featureInspectionEnabledRef = useRef(featureInspectionEnabled);
  const featureSelectionEnabledRef = useRef(featureSelectionEnabled);
  const featureSelectionMethodRef = useRef(featureSelectionMethod);
  const selectedFeaturesRef = useRef(selectedFeatures);
  const onViewChangeRef = useRef(onViewChange);
  const onFeatureSelectRef = useRef(onFeatureSelect);
  const onFeaturesSelectRef = useRef(onFeaturesSelect);
  const annotationAuthoringEnabledRef = useRef(annotationAuthoringEnabled);
  const annotationCreationToolRef = useRef(annotationCreationTool);
  const activeAnnotationLayerIdRef = useRef(activeAnnotationLayerId);
  const annotationDraftCoordinatesRef = useRef(annotationDraftCoordinates);
  const onAnnotationCoordinateRef = useRef(onAnnotationCoordinate);
  const onAnnotationSelectRef = useRef(onAnnotationSelect);
  const onAnnotationDeselectRef = useRef(onAnnotationDeselect);
  const onAnnotationMoveRef = useRef(onAnnotationMove);

  useEffect(() => {
    basemapRef.current = basemap;
  }, [basemap]);

  useEffect(() => {
    backgroundColourRef.current = backgroundColour;
  }, [backgroundColour]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    onFeatureInspectRef.current = onFeatureInspect;
  }, [onFeatureInspect]);

  useEffect(() => {
    featureInspectionEnabledRef.current = featureInspectionEnabled;
  }, [featureInspectionEnabled]);

  useEffect(() => {
    featureSelectionEnabledRef.current = featureSelectionEnabled;
  }, [featureSelectionEnabled]);

  useEffect(() => {
    featureSelectionMethodRef.current = featureSelectionMethod;
  }, [featureSelectionMethod]);

  useEffect(() => {
    selectedFeaturesRef.current = selectedFeatures;
  }, [selectedFeatures]);

  useEffect(() => {
    onViewChangeRef.current = onViewChange;
  }, [onViewChange]);

  useEffect(() => {
    onFeatureSelectRef.current = onFeatureSelect;
  }, [onFeatureSelect]);

  useEffect(() => {
    onFeaturesSelectRef.current = onFeaturesSelect;
  }, [onFeaturesSelect]);

  useEffect(() => {
    annotationAuthoringEnabledRef.current = annotationAuthoringEnabled;
    annotationCreationToolRef.current = annotationCreationTool;
    activeAnnotationLayerIdRef.current = activeAnnotationLayerId;
    annotationDraftCoordinatesRef.current = annotationDraftCoordinates;
    onAnnotationCoordinateRef.current = onAnnotationCoordinate;
    onAnnotationSelectRef.current = onAnnotationSelect;
    onAnnotationDeselectRef.current = onAnnotationDeselect;
    onAnnotationMoveRef.current = onAnnotationMove;
  }, [
    activeAnnotationLayerId,
    annotationAuthoringEnabled,
    annotationCreationTool,
    annotationDraftCoordinates,
    onAnnotationCoordinate,
    onAnnotationMove,
    onAnnotationSelect,
    onAnnotationDeselect,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const adapter = createAdapter();
    let resizeObserver: ResizeObserver | undefined;
    let isActive = true;

    try {
      adapter.mount(container, {
        interactive,
        renderWorldCopies,
        ...(observesGround
          ? {
              onGroundFootprint: (footprint: GroundFootprint | null, zoom: number) =>
                footprintCallbackRef.current?.(footprint, zoom),
            }
          : {}),
        basemap: basemapRef.current,
        backgroundColour: backgroundColourRef.current,
        view: viewRef.current,
        pitchEnabled,
        ...(minimumZoom === undefined ? {} : { minimumZoom }),
        ...(maximumZoom === undefined ? {} : { maximumZoom }),
        scene: sceneRef.current,
        featureInspectionEnabled: featureInspectionEnabledRef.current,
        featureSelectionEnabled: featureSelectionEnabledRef.current,
        ...(featureSelectionMethodRef.current === undefined
          ? {}
          : { featureSelectionMethod: featureSelectionMethodRef.current }),
        selectedFeatures: selectedFeaturesRef.current,
        onFeatureInspect: (feature) => onFeatureInspectRef.current?.(feature),
        onFeatureSelect: (feature, mutation) => onFeatureSelectRef.current?.(feature, mutation),
        onFeaturesSelect: (features, mutation) => onFeaturesSelectRef.current?.(features, mutation),
        annotationAuthoringEnabled: annotationAuthoringEnabledRef.current,
        annotationCreationTool: annotationCreationToolRef.current,
        activeAnnotationLayerId: activeAnnotationLayerIdRef.current,
        annotationDraftCoordinates: annotationDraftCoordinatesRef.current,
        onAnnotationCoordinate: (coordinate, finish) =>
          onAnnotationCoordinateRef.current?.(coordinate, finish),
        onAnnotationDeselect: () => onAnnotationDeselectRef.current?.(),
        onAnnotationSelect: (layerId, annotationId) =>
          onAnnotationSelectRef.current?.(layerId, annotationId),
        onAnnotationMove: (
          layerId,
          annotationId,
          longitudeDelta,
          latitudeDelta,
          part,
          widthDelta,
          heightDelta,
        ) =>
          onAnnotationMoveRef.current?.(
            layerId,
            annotationId,
            longitudeDelta,
            latitudeDelta,
            part,
            widthDelta,
            heightDelta,
          ),
        onReady: () => setStatus('ready'),
        onError: (error) => {
          console.error('Placeio Maps could not render the current project scene.', error);
          setStatus('error');
        },
        onBasemapStatusChange: (nextStatus) => {
          if (isActive) setBasemapStatus(nextStatus);
        },
        onRenderedFeaturesChange: (featureCount) => {
          if (isActive) setRenderedFeatureCount(featureCount);
        },
        onRenderedLabelsChange: (labelCount) => {
          if (isActive) setRenderedLabelCount(labelCount);
        },
        onRendered3dBuildingFeaturesChange: (featureCount) => {
          if (isActive) setRendered3dBuildingCount(featureCount);
        },
        onRenderedTopLayerChange: (layerId) => {
          if (isActive) setRenderedTopLayerId(layerId);
        },
        onRenderedStyleChange: (inspection) => {
          if (isActive) setRenderedStyle(inspection);
        },
        onInspectionHighlightChange: (geometry) => {
          if (isActive) setInspectionHighlight(geometry ?? '');
        },
        onSelectionHighlightChange: (featureCount) => {
          if (isActive) setSelectionHighlightCount(featureCount);
        },
        onViewChange: (nextView, metadata) => onViewChangeRef.current(nextView, metadata),
      });
      adapterRef.current = adapter;

      if (typeof ResizeObserver === 'function') {
        resizeObserver = new ResizeObserver(() => adapter.resize());
        resizeObserver.observe(container);
      }
    } catch (error) {
      console.error('Placeio Maps could not initialise the rendering surface.', error);
      queueMicrotask(() => {
        if (isActive) setStatus('error');
      });
    }

    return () => {
      isActive = false;
      adapterRef.current = null;
      resizeObserver?.disconnect();
      adapter.destroy();
    };
  }, [
    createAdapter,
    maximumZoom,
    minimumZoom,
    pitchEnabled,
    interactive,
    observesGround,
    renderWorldCopies,
  ]);

  useEffect(() => {
    adapterRef.current?.setBasemap?.(basemap);
  }, [basemap]);

  useEffect(() => {
    adapterRef.current?.setImportRectangle?.(importRectangle);
  }, [importRectangle]);

  useEffect(() => {
    adapterRef.current?.setBackgroundColour(backgroundColour);
  }, [backgroundColour]);

  useEffect(() => {
    adapterRef.current?.setView(view);
  }, [view]);

  useEffect(() => {
    adapterRef.current?.setFeatureInspectionEnabled?.(featureInspectionEnabled);
  }, [featureInspectionEnabled]);

  useEffect(() => {
    if (featureSelectionMethod !== undefined) {
      adapterRef.current?.setFeatureSelectionMethod?.(featureSelectionMethod);
    } else {
      adapterRef.current?.setFeatureSelectionEnabled?.(featureSelectionEnabled);
    }
  }, [featureSelectionEnabled, featureSelectionMethod]);

  useEffect(() => {
    adapterRef.current?.setInspectedFeature?.(inspectedFeature);
  }, [inspectedFeature]);

  useEffect(() => {
    adapterRef.current?.setSelectedFeatures?.(selectedFeatures);
  }, [selectedFeatures]);

  useEffect(() => {
    adapterRef.current?.setAnnotationInteraction?.(
      annotationAuthoringEnabled,
      annotationCreationTool,
      activeAnnotationLayerId,
      annotationDraftCoordinates,
    );
  }, [
    activeAnnotationLayerId,
    annotationAuthoringEnabled,
    annotationCreationTool,
    annotationDraftCoordinates,
  ]);

  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;

    try {
      adapter.setScene(scene);
    } catch (error) {
      console.error('Placeio Maps could not render the current project scene.', error);
      queueMicrotask(() => setStatus('error'));
    }
  }, [scene]);

  return (
    <div
      aria-label="Placeio Maps map surface"
      className="map-surface"
      data-symbol-status={
        symbolError
          ? 'error'
          : preparedSymbols.assetsKey !== assetsKey || preparedSymbols.signature !== symbolSignature
            ? 'preparing'
            : 'ready'
      }
      data-symbol-image-count={symbolImages.size}
      data-rendered-feature-count={renderedFeatureCount}
      data-rendered-label-count={renderedLabelCount}
      data-rendered-3d-building-count={rendered3dBuildingCount}
      data-inspection-highlight={inspectionHighlight}
      data-selection-highlight-count={selectionHighlightCount}
      data-selection-method={featureSelectionMethod ?? (featureSelectionEnabled ? 'click' : '')}
      data-selection-tool-active={
        featureSelectionMethod !== undefined
          ? featureSelectionMethod
            ? 'true'
            : 'false'
          : featureSelectionEnabled
            ? 'true'
            : 'false'
      }
      data-rendered-layer-count={status === 'ready' ? layers.length : 0}
      data-rendered-layer-order={renderedStyle.layerIds.join(',')}
      data-rendered-top-layer={renderedTopLayerId}
      data-rendered-background-colour={renderedStyle.backgroundColour}
      data-rendered-polygon-label-anchor-count={renderedStyle.polygonLabelAnchorCount}
      data-rendered-style-colours={renderedStyle.colours.join(',')}
      data-rendered-style-signature={renderedStyle.signature}
      data-map-status={symbolError ? 'error' : status}
      data-annotation-tool={annotationCreationTool ?? ''}
      data-annotation-layer-count={annotationLayers.length}
      data-annotation-render-scale={annotationRenderScale}
      data-presentation-scale={presentationScale}
      data-basemap-preset={basemap.presetId}
      data-basemap-status={basemapStatus}
      data-map-view-center={`${view.center[0]},${view.center[1]}`}
      data-map-view-zoom={view.zoom}
      data-map-view-bearing={view.bearing}
      data-map-view-pitch={view.pitch}
      ref={containerRef}
      role="application"
    >
      {symbolError ? (
        <p role="alert" className="map-status">
          {symbolError}
        </p>
      ) : null}
      <span className="map-status" role="status">
        {status === 'ready' ? 'Map renderer ready' : `Map renderer ${status}`}
      </span>
      {basemapStatus === 'error' ? (
        <span className="basemap-error" role="alert">
          Basemap unavailable. Your project data is unchanged.
        </span>
      ) : null}
      {showAttribution && attribution.length > 0 ? (
        <span className="basemap-attribution" aria-label="Basemap attribution">
          {attribution.map((link, index) => (
            <span key={link.label}>
              {index > 0 ? ' · ' : ''}
              <a href={link.url} rel="noreferrer" target="_blank">
                {link.label}
              </a>
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}
